// src/pages/AgentPage/MessageFileAttachment.tsx
// Вложение к сообщению агента: карточка созданного файла со скачиванием
// и индикатор «Генерирую файл…» во время стриминга маркера CREATE_FILE.
import { useState } from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { Download } from 'lucide-react';
import { listCreatedFilesSync } from 'services/demoApi/damApi';
import { downloadDamFile } from 'services/demoApi/fileDownload';
import type { AgentAttachment } from 'hooks/useAgentChatSession';
import type { StreamingFileInfo } from 'services/demoApi/agentFileMarker';
import { FileExtIcon } from 'pages/DataSourcesPage/fileIcons';

const CARD_SX = {
  mt: 1,
  px: 1.5,
  py: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border)',
  bgcolor: '#f8fafc',
  maxWidth: 360,
};

/** Карточка файла, прикреплённого к сообщению агента */
export function MessageFileAttachment({ attachment }: { attachment: AgentAttachment }) {
  const [downloading, setDownloading] = useState(false);
  const stored = attachment.fileId
    ? (listCreatedFilesSync().find((s) => s.file.id === attachment.fileId)?.file ?? null)
    : null;

  const handleDownload = () => {
    if (!stored) return;
    setDownloading(true);
    downloadDamFile(stored)
      .catch(() => undefined)
      .finally(() => setDownloading(false));
  };

  return (
    <Box sx={CARD_SX}>
      <FileExtIcon ext={attachment.ext} size={18} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text-primary)' }}
        >
          {attachment.name}.{attachment.ext}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: stored ? '#059669' : 'var(--color-text-muted)' }}>
          {stored ? 'Создан агентом · в рабочей папке' : 'Файл удалён из папки'}
        </Typography>
      </Box>
      {stored && (
        <IconButton
          size="small"
          aria-label={`Скачать ${attachment.name}.${attachment.ext}`}
          disabled={downloading}
          onClick={handleDownload}
          sx={{
            p: 0.5,
            flexShrink: 0,
            color: 'var(--color-text-muted)',
            '&:hover': { color: 'var(--color-primary)' },
          }}
        >
          {downloading ? <CircularProgress size={14} /> : <Download size={15} />}
        </IconButton>
      )}
    </Box>
  );
}

/** Индикатор генерации файла в стриминг-пузыре */
export function FileGeneratingIndicator({ info }: { info: StreamingFileInfo }) {
  return (
    <Box sx={{ ...CARD_SX, borderStyle: 'dashed', bgcolor: 'rgba(20,116,255,0.04)' }}>
      {info.ext ? <FileExtIcon ext={info.ext} size={18} /> : <CircularProgress size={16} />}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text-primary)' }}
        >
          {info.name ? `${info.name}.${info.ext}` : 'Готовлю файл…'}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: 'var(--color-primary)' }}>
          Генерирую файл…
        </Typography>
      </Box>
      {info.ext && <CircularProgress size={14} sx={{ flexShrink: 0 }} />}
    </Box>
  );
}
